import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from 'react-router-dom';
import SidebarLinkGroup from "./SidebarLinkGroup";
import Logo from '../../assets/images/logo/logo.svg';
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/rbac";
import { permissions } from "@/rbac/permissions";
import route from "@/constants/route";
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface SidebarProps {
    sidebarOpen: boolean;
    setSidebarOpen: (arg: boolean) => void;
}

const Sidebar = ({sidebarOpen, setSidebarOpen}: SidebarProps) => {
    const {t} = useTranslation()
    const location = useLocation();
    const { pathname } = location;
    const { hasPermission } = usePermissions();
    const { auth } = useSelector((state: RootState) => state.auth);

    const trigger = useRef<any>(null);
    const sidebar = useRef<any>(null);

    const storedSidebarExpanded = localStorage.getItem('sidebar-expanded');
    const [sidebarExpanded, setSidebarExpanded] = useState(
        storedSidebarExpanded === null ? false : storedSidebarExpanded === 'true'
    );

    // // close on click outside
    // useEffect(() => {
    //     const clickHandler = ({ target }: MouseEvent) => {
    //         if (!sidebar.current || !trigger.current) return;
    //         if (
    //             !sidebarOpen ||
    //             sidebar.current.contains(target) ||
    //             trigger.current.contains(target)
    //         )
    //             return;
    //         setSidebarOpen(false);
    //     };
    //     document.addEventListener('click', clickHandler);
    //     return () => document.removeEventListener('click', clickHandler);
    // });

    // // close if the esc key is pressed
    // useEffect(() => {
    //     const keyHandler = ({ keyCode }: KeyboardEvent) => {
    //     if (!sidebarOpen || keyCode !== 27) return;
    //         setSidebarOpen(false);
    //     };
    //     document.addEventListener('keydown', keyHandler);
    //     return () => document.removeEventListener('keydown', keyHandler);
    // });

    useEffect(() => {
        localStorage.setItem('sidebar-expanded', sidebarExpanded.toString());
        if (sidebarExpanded) {
            document.querySelector('body')?.classList.add('sidebar-expanded');
        } else {
            document.querySelector('body')?.classList.remove('sidebar-expanded');
        }
    }, [sidebarExpanded]);

    return (
        <aside
            ref={sidebar}
            className={`absolute left-0 top-0 z-99 flex h-screen w-72.5 flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark ${
                sidebarOpen ? 'lg:static translate-x-0' : '-translate-x-full'
            }`}
        >
            {/* <!-- SIDEBAR HEADER --> */}
            <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
                <NavLink to="/dashboard" className="flex items-center">
                    <img src={Logo} alt="Logo" />
                    <span className="uppercase text-white font-bold text-2xl ms-2">sckyroom</span>
                </NavLink>

                <button
                    ref={trigger}
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    aria-controls="sidebar"
                    aria-expanded={sidebarOpen}
                    className="block lg:hidden"
                >
                    <svg
                        className="fill-current"
                        width="20"
                        height="18"
                        viewBox="0 0 20 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                        d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
                        fill=""
                        />
                    </svg>
                </button>
            </div>
            {/* <!-- SIDEBAR HEADER --> */}

            <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
                {/* <!-- Sidebar Menu --> */}
                <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
                    {/* <!-- Menu Group --> */}
                    <div>
                        <ul className="mb-6 flex flex-col gap-1.5">
                        {/* <!-- Menu Item Dashboard --> */}
                        <li>
                            <NavLink
                                to={route.DASHBOARD}
                                className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                    pathname.includes('dashboard') && 'bg-graydark dark:bg-meta-4'
                                }`}
                            >
                                <i className="fa-light fa-gauge" />
                                {t("menu.dashboard")}
                            </NavLink>
                        </li>
                        {/* <li>
                            <NavLink
                                to={route.CASHFLOW}
                                className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                    pathname.includes('cashflow') && 'bg-graydark dark:bg-meta-4'
                                }`}
                            >
                                <i className="fa-light fa-money-check-dollar" />
                                {t("menu.cashflow")}
                            </NavLink>
                        </li> */}

                        {/* <!-- Menu Item Transaction --> */}
                        {
                            hasPermission(permissions.TRANSACTION.VIEW) && <li>
                                <NavLink
                                    to={route.TRANSACTIONS.VIEW}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('/transactions') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-money-bill" />
                                    {t("menu.transaction")}
                                </NavLink>
                            </li>
                        }
                        <hr />

                        {/* <!-- Menu Item House Rental --> */}
                        {
                            hasPermission(permissions.RENTAL.VIEW) && <li>
                                <NavLink
                                    to={route.RENTALS.VIEW}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('/rentals') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-rectangle-history" />
                                    {t("menu.rental")}
                                </NavLink>
                            </li>
                        }

                         {/* <!-- Menu Item User --> */}
                         {
                            hasPermission(permissions.HOUSERENTAL.VIEW) && <li>
                                <NavLink
                                    to={route.HOUSERENTALS.VIEW}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('/house-rentals') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-house" />
                                    {t("menu.house_rental")}
                                </NavLink>
                            </li>
                        }
                        {/* <!-- Menu Item User --> */}

                        <hr />


                        {/* <!-- Menu Item Users - SuperAdmin and Admin only --> */}
                        {
                            auth.role <= 2 && hasPermission(permissions.USER.VIEW) && <li>
                                <NavLink
                                    to={route.USERS.VIEW}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('users') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-user" />
                                    {t("menu.user")}
                                </NavLink>
                            </li>
                        }
                        {/* <!-- Menu Item Users --> */}

                        {
                            hasPermission(permissions.TRANSACTION.VIEW) && <li>
                                <NavLink
                                    to={route.TRANSACTIONSQUERY.VIEW}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('/transaction-queries') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-database" />
                                    {t("menu.report")}
                                </NavLink>
                            </li>
                        }

                        {/* <!-- Menu Item KPI - Admin and SuperAdmin only --> */}
                        {
                            hasPermission(permissions.KPI.VIEW) && <li>
                                <NavLink
                                    to={route.KPI}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname === route.KPI && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-chart-simple" />
                                    {t("menu.kpi")}
                                </NavLink>
                            </li>
                        }

                        {/* <!-- Menu Item Settings --> */}
                        {
                            (hasPermission(permissions.UNITCODE.VIEW) || hasPermission(permissions.BUSINESSUNITS.VIEW) || hasPermission(permissions.TRANSACTIONTYPE.VIEW) || hasPermission(permissions.PERMISSION.VIEW)) && <SidebarLinkGroup
                                activeCondition={
                                pathname === '/settings' || pathname.includes('settings')
                                }
                            >
                                {(handleClick, open) => {
                                return (
                                    <React.Fragment>
                                    <NavLink
                                        to="#"
                                        className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        (pathname === '/settings' ||
                                            pathname.includes('settings')) &&
                                        'bg-graydark dark:bg-meta-4'
                                        }`}
                                        onClick={(e) => {
                                        e.preventDefault();
                                        sidebarExpanded
                                            ? handleClick()
                                            : setSidebarExpanded(true);
                                        }}
                                    >
                                        <i className="fa-light fa-gear" />
                                        {t("menu.setting")}
                                        <i className={`fa-regular fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                                            open && 'fa-rotate-180'
                                        }`} />
                                    </NavLink>

                                    {
                                        hasPermission(permissions.TENANT.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.TENANT.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-user-police" />
                                                {t("menu.tenant")}
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }

                                    {/* <!-- Dropdown Setting Start --> */}
                                    {
                                        hasPermission(permissions.BUSINESSUNITS.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.BUSINESSUNITS.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-hotel" />
                                                {t("menu.business_unit")}
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }
                                    {
                                        hasPermission(permissions.UNITCODE.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.UNITCODES.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-wallet" />
                                                {t("menu.unitcode")}
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }
                                    {
                                        hasPermission(permissions.TRANSACTIONTYPE.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.TRANSACTIONTYPES.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-credit-card" />
                                                {t("menu.transaction_type")}
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }
                                    {
                                        hasPermission(permissions.EXTERNALAPI.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.EXTERNALAPI.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-key" />
                                                External API
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }
                                    {
                                        hasPermission(permissions.PERMISSION.VIEW) && <div
                                        className={`translate transform overflow-hidden ${
                                            !open && 'hidden'
                                        }`}
                                        >
                                        <ul className="my-4 flex flex-col gap-2.5 pl-6">
                                            <li>
                                            <NavLink
                                                to={route.SETTINGS.PERMISSIONS.VIEW}
                                                className={({ isActive }) =>
                                                'group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-bodydark2 duration-300 ease-in-out hover:text-white ' +
                                                (isActive && '!text-white')
                                                }
                                            >
                                                <i className="fa-light fa-universal-access" />
                                                {t("menu.permission")}
                                            </NavLink>
                                            </li>
                                        </ul>
                                        </div>
                                    }
                                    {/* <!-- Dropdown Setting End --> */}
                                    </React.Fragment>
                                )
                                }}
                            </SidebarLinkGroup>
                        }
                        {/* <!-- Menu Item Settings --> */}

                        {/* <!-- Menu Item Audit Log - SuperAdmin and Admin only --> */}
                        {
                            auth.role <= 2 && hasPermission(permissions.AUDITLOG.VIEW) && <li>
                                <NavLink
                                    to={route.AUDITLOGS}
                                    className={`group relative flex items-center gap-2.5 rounded-sm py-2 px-4 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-graydark dark:hover:bg-meta-4 ${
                                        pathname.includes('auditlogs') && 'bg-graydark dark:bg-meta-4'
                                    }`}
                                >
                                    <i className="fa-light fa-rectangle-history" />
                                    {t("menu.auditlog")}
                                </NavLink>
                            </li>
                        }
                        {/* <!-- Menu Item User --> */}
                        </ul>
                    </div>
                </nav>
                {/* <!-- Sidebar Menu --> */}
            </div>
        </aside>
    );
};

export default Sidebar;
